<template>
  <v-container class="pa-0">
    <v-card class="elevation-0 py-4 px-5">
      <v-row class="ma-0">
        <v-card-title>Agregar Vialidad</v-card-title>
        <v-spacer/>
        <v-switch v-model="live.is_active" label="Activo" inset />
      </v-row>

      <v-form>
        <v-row class="ma-0">
          <v-col cols="12" class="py-0">
            <v-text-field
              :rules="[rules.required]"
              class="github_dark"
              name="name"
              label="Título"
              outlined
              v-model="live.name"
              dense
            />
          </v-col>

          <!-- Autocomplete de TAGS (filtrado local) -->
          <v-col cols="12" class="py-0">
            <v-autocomplete
              v-model="live.live_tag_id"
              :items="tagsItems"
              :loading="isLoadingTags"
              :search-input.sync="tagSearch"
              item-text="name"
              item-value="id"
              label="Tags"
              placeholder="Escribe para buscar o crear"
              outlined
              hide-selected
              clearable
              dense
              :hide-no-data="!tagSearchTrim"
            >
              <!-- Sin resultados: mensaje + botón que abre el diálogo de creación -->
              <template v-slot:no-data>
                <div class="pa-3">
                  <v-row class="ma-0">
                    <div class="mb-2">
                      No se encontraron resultados para
                      <strong>“{{ tagSearch }}”</strong>
                    </div>
                    <v-spacer/>
                    <v-btn
                      small
                      color="primary"
                      class="elevation-0"
                      :disabled="!tagSearchTrim"
                      @click="openTagDialog()"
                    >
                      Crear tag
                    </v-btn>
                  </v-row>
                </div>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- Código embebido -->
          <v-col cols="12" class="py-0">
            <CodeEditor
              v-model="live.embedded_code"
              :languages="[['html', 'HTML']]"
              width="100%"
              min_height="100px"
              class="mb-6"
            />
            <v-alert type="warning" border="left" icon="mdi-alert-circle" text>
              <strong>Las siguientes propiedades deben de estar incluidas:</strong><br />
              <code>style="position:absolute; top:0; left:0;width:100%; height:100%;"</code>
            </v-alert>
          </v-col>
        </v-row>
      </v-form>

      <v-card-actions class="px-0">
        <v-btn @click="close()" class="elevation-0 px-4" text>
          <strong>Cancelar</strong>
        </v-btn>
        <v-btn :disabled="ready || gris" @click="save()" class="elevation-0 px-4" color="primary">
          <strong>Guardar</strong>
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Diálogo: Crear Tag -->
    <v-dialog v-model="tagDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">Crear Tag</v-card-title>
        <v-card-text>
          <v-form ref="tagFormRef" lazy-validation>
            <v-text-field
              v-model="tagForm.name"
              label="Tag"
              :rules="[rules.required]"
              outlined dense
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="elevation-0" @click="tagDialog=false">Cancelar</v-btn>
          <v-btn color="primary" class="elevation-0" :loading="creatingTag" @click="submitCreateTag">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar :color="snackbar.color" v-model="snackbar.show">
      <strong>{{ snackbar.message }}</strong>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import CodeEditor from "simple-code-editor";

export default {
  components: { CodeEditor },
  data: () => ({
    gris: false,
    live: {
      name: "",
      embedded_code: "",
      is_active: false,
      type: "vialidad",
      live_tag_id: "" // IDs seleccionados
    },
    snackbar: {
      show: false,
      message: null,
      color: null,
    },

    // TAGS (carga única + filtrado local del v-autocomplete)
    tagsItems: [],        // catálogo {id, name}
    tagSearch: "",        // texto que escribe el usuario (sólo para mostrar no-data)
    isLoadingTags: false, // loader de la carga inicial

    // Diálogo de creación de tag
    tagDialog: false,
    creatingTag: false,
    tagForm: {
      name: "",
    },
  }),
  computed: {
    rules() {
      return {
        required: (value) => !!String(value || "").trim() || "Campo requerido.",
      };
    },
    ready() {
      // Si quieres que 'tags' sea obligatorio, agrega: && this.live.tags.length > 0
      return !(String(this.live.name).trim() && String(this.live.embedded_code).trim());
    },
    tagSearchTrim() {
      return (this.tagSearch || "").trim();
    },
  },
  methods: {
    // ==== Carga única de todos los tags ====
    async fetchAllTags() {
      try {
        this.isLoadingTags = true;
        const { data } = await axios.get(
          process.env.VUE_APP_BACKEND_ROUTE + "api/v1/live_tags"
        );
        const arr = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        this.tagsItems = arr.map(x => ({
          id: x.id ?? x?.attributes?.id,
          name: x.name ?? x?.attributes?.name,
        })).filter(t => t.id && t.name);
      } catch (e) {
        // opcional: notificar
      } finally {
        this.isLoadingTags = false;
      }
    },

    openTagDialog() {
      this.tagForm = {
        name: this.tagSearchTrim, // prellenado
      };
      this.tagDialog = true;
      this.$nextTick(() => this.$refs.tagFormRef && this.$refs.tagFormRef.resetValidation());
    },

    async submitCreateTag() {
      if (!this.tagForm.name || !String(this.tagForm.name).trim()) return;
      try {
        this.creatingTag = true;
        const payload = {
          name: String(this.tagForm.name).trim(),
        };

        const { data } = await axios.post(
          process.env.VUE_APP_BACKEND_ROUTE + "api/v1/live_tags",
          payload,
          { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
        );

        const created = {
          id: data?.data?.id ?? data?.id,
          name: data?.data?.name ?? data?.name ?? payload.name,
        };

        // Agregar al catálogo y seleccionar
        if (!this.tagsItems.find(t => t.id == created.id)) {
          this.tagsItems = [created, ...this.tagsItems];
        }
        if (!this.live.tags.includes(created.id)) {
          this.live.live_tag_id = created.id;
        }

        this.snackbar = { show: true, color: "success", message: `Tag “${created.name}” creado` };
        this.tagDialog = false;
        this.tagSearch = "";
      } catch (error) {
        this.snackbar = {
          show: true,
          color: "error",
          message: error?.response?.data?.message || "No se pudo crear el tag.",
        };
      } finally {
        this.creatingTag = false;
      }
    },

    // ==== Guardar live ====
    save() {
      const body = {
        name: this.live.name,
        embedded_code: this.live.embedded_code,
        is_active: this.live.is_active ? 1 : 0,
        live_tag_id: this.live.live_tag_id, // ids
        type: "vialidad",
      };

      this.gris = true;
      axios
        .post(process.env.VUE_APP_BACKEND_ROUTE + "api/v1/live", body, {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        })
        .then(() => {
          this.close();
        })
        .catch((error) => {
          this.snackbar = {
            message: error.response?.data?.message || "Error al guardar",
            color: "error",
            show: true,
          };
          this.gris = false;
        });
    },

    close() {
      this.live = { name: "", embedded_code: "", is_active: false, tags: [] };
      this.tagSearch = "";
      this.gris = false;
      this.$nextTick(() => this.$emit("closeCreateDialog", false));
    },
  },
  created() {
    // Carga el catálogo una sola vez
    this.fetchAllTags();
  },
};
</script>
